<script setup>
import Dialog from 'primevue/dialog';
import {computed, inject, reactive, ref, watch} from "vue";
import {useBreakpoint} from "@/composables/useBreakpoint";
import {Icon} from "@iconify/vue";
import FloatLabel from "primevue/floatlabel";
import Select from "primevue/select";
import {numeric, required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import {useConfigStore} from "@/stores/config.js";
import {InputGroup, InputGroupAddon, InputText, InputNumber, Textarea} from "primevue";
import {showMessage} from "@/utils/showMessage.js";
import {useCurrencyBuyAdsStore} from "@/stores/currencyBuyAds.js";
import {keepNonEmptyValues} from "@/utils/helpers.js";

const emit = defineEmits(['refresh']);
const visible = inject('isShowAddNewAdModal')

const props = defineProps({
  selectedRecord: Object
})

const adsStore = useCurrencyBuyAdsStore();
const configStore = useConfigStore()
const {isXL} = useBreakpoint();
const saving = ref(false)
const ad = reactive({
  "game": props.selectedRecord?.game ? props.selectedRecord.game : "",
  "server": props.selectedRecord?.server ? props.selectedRecord.server : "",
  "description": props.selectedRecord?.description ? props.selectedRecord.description : "",
  "amount": props.selectedRecord?.amount ? props.selectedRecord.amount : "",
  "tomanFori": props.selectedRecord?.tomanFori ? props.selectedRecord.tomanFori : "",
  "toman": props.selectedRecord?.toman ? props.selectedRecord.toman : "",
  "tomanTetheri": props.selectedRecord?.tomanTetheri ? props.selectedRecord.tomanTetheri : "",
  "unit": props.selectedRecord?.unit ? props.selectedRecord.unit : 1,
  "minimum": props.selectedRecord?.minimum ? props.selectedRecord.minimum : null,
  "maximum": props.selectedRecord?.maximum ? props.selectedRecord.maximum : null
})
const rules = computed(() => {
  return {
    game: {required},
    server: {required},
    amount: {required, numeric},
    tomanFori: {numeric},
    toman: {required, numeric},
    tomanTetheri: {numeric},
    unit: {required, numeric}
  };
});
const $vulidate = useVuelidate(rules, ad);

/* ---------- Select options ---------- */
const games = computed(() => configStore.getGamesByService('Currency') || [])

const selectedGame = computed(() => games.value.find(g => g.game === ad.game) || null)

const servers = computed(() => selectedGame.value ? selectedGame.value.servers : [])

const onGameChange = () => {
  ad.server = servers.value.length === 1 ? servers.value[0] : ''
}

const addNewAd = async () => {
  //default value for unit is 1
  ad.unit = ad.unit ? ad.unit : 1

  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    return false;
  }

  /*const ok = await showAlert('You are adding new order?', 'Are you sure?', 'Yes', 'No')
  if (!ok) return*/

  const payload = keepNonEmptyValues(ad)
  if (!payload) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  try {
    saving.value = true
    const res = await adsStore.action_addAd(payload)
    if (res.result === true) {
      if (props.selectedRecord?.game) {
        showMessage('Ad Edited!', 'success')
      } else {
        showMessage('New Ad added!', 'success')
      }
      emit('refresh', true)
      visible.value = false;
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

function resetAd() {
  ad.game = "";
  ad.server = "";
  ad.description = "";
  ad.amount = "";
  ad.tomanFori = "";
  ad.toman = "";
  ad.tomanTetheri = "";
  ad.unit = 1;
  ad.minimum = "";
  ad.maximum = "";
  $vulidate.value.$reset()
}

/* ---------- Watch for Game Change ---------- */
watch(
    () => ad.game,
    () => onGameChange()
)
</script>

<template>
  <Dialog v-model:visible="visible" @hide="resetAd"
          :class="['min-w-max min-h-max !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        {{ selectedRecord?.game ? "Edit Ad" : "Add New Ad" }}
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 text-xs 2xl:text-sm">
      <div class="grid grid-cols-3 gap-4">
        <!-- ================== Game ================== -->
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel class="w-full custom-select"
                      :class="{ 'has-error': $vulidate.game.$error }"
                      variant="on">
            <Select v-model="ad.game" inputId="game" :options="games" optionLabel="game"
                    option-value="game"
                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                    :class="[$vulidate.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                    input-class="!text-xs 2xl:!text-sm"
                    filter autoFilterFocus/>
            <label for="game" class="!bg-slate-900">Game</label>
          </FloatLabel>
          <template v-if="$vulidate.game.$error">
            <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
               v-if="!$vulidate.game.required.$response">
              This field is required
            </p>
          </template>
        </div>

        <!-- ================== Server ================== -->
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel class="w-full custom-select"
                      :class="{ 'has-error': $vulidate.server.$error }"
                      variant="on">
            <Select v-model="ad.server" inputId="server" :options="servers"
                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                    :class="[$vulidate.server.$error ? '!border !border-red-300' : '!border-slate-700']"
                    input-class="!text-xs 2xl:!text-sm"
                    :virtualScrollerOptions="{ itemSize: 38 }"
                    filter autoFilterFocus/>
            <label for="server" class="!bg-slate-900">Server</label>
          </FloatLabel>
          <template v-if="$vulidate.server.$error">
            <p class="text-red-300 mt-1 text-xs"
               v-if="!$vulidate.server.required.$response">
              This field is required
            </p>
          </template>
        </div>

        <div class="flex flex-col gap-0.5">
          <FloatLabel variant="on"
                      :class="{ 'has-error': $vulidate.amount.$error }">
            <InputText v-allow-digits-dot id="amount" v-model="ad.amount" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vulidate.amount.$error }"/>
            <label for="amount" class="!bg-slate-900"
                   :class="{ 'has-error': $vulidate.amount.$error }">Amount</label>
          </FloatLabel>
          <template v-if="$vulidate.amount.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vulidate.amount.required.$response">
              This field is required
            </p>
          </template>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-4">
        <div class="flex flex-col gap-0.5">
          <InputGroup>
            <FloatLabel variant="on"
                        :class="{ 'has-error': $vulidate.toman.$error }">
              <InputText v-allow-digits-dot id="toman" v-model="ad.toman" autocomplete="off"
                         class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                         :class="{ 'has-error has-addon': $vulidate.toman.$error }"/>
              <label for="toman" class="!bg-slate-900"
                     :class="{ 'has-error': $vulidate.toman.$error }">Toman</label>
            </FloatLabel>
            <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">Tmn
            </InputGroupAddon>
          </InputGroup>
          <template v-if="$vulidate.toman.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vulidate.toman.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="flex flex-col gap-0.5">
          <InputGroup>
            <FloatLabel variant="on"
                        :class="{ 'has-error': $vulidate.tomanFori.$error }">
              <InputText v-allow-digits-dot id="tomanFori" v-model="ad.tomanFori" autocomplete="off"
                         class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                         :class="{ 'has-error has-addon': $vulidate.tomanFori.$error }"/>
              <label for="tomanFori" class="!bg-slate-900"
                     :class="{ 'has-error': $vulidate.tomanFori.$error }">Toman Fori</label>
            </FloatLabel>
            <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">Tmn
            </InputGroupAddon>
          </InputGroup>
          <template v-if="$vulidate.tomanFori.$error">
            <p class="text-red-300 mt-1 text-xs"
               v-if="!$vulidate.tomanFori.numeric.$response">
              This field should be a number
            </p>
          </template>
        </div>
        <div class="flex flex-col gap-0.5">
          <InputGroup>
            <FloatLabel variant="on"
                        :class="{ 'has-error': $vulidate.tomanTetheri.$error }">
              <InputText v-allow-digits-dot id="tomanTetheri" v-model="ad.tomanTetheri" autocomplete="off"
                         class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                         :class="{ 'has-error has-addon': $vulidate.tomanTetheri.$error }"/>
              <label for="tomanTetheri" class="!bg-slate-900"
                     :class="{ 'has-error': $vulidate.tomanTetheri.$error }">Toman Tetheri</label>
            </FloatLabel>
            <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">Tmn
            </InputGroupAddon>
          </InputGroup>
          <template v-if="$vulidate.tomanTetheri.$error">
            <p class="text-red-300 mt-1 text-xs"
               v-if="!$vulidate.tomanTetheri.numeric.$response">
              This field should be a number
            </p>
          </template>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-4">
        <div class="flex flex-col gap-0.5">
          <FloatLabel variant="on"
                      :class="{ 'has-error': $vulidate.unit.$error }">
            <InputNumber v-allow-digits input-id="unit" v-model="ad.unit" autocomplete="off"
                         class="w-full"
                         input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                         :class="{ 'has-error': $vulidate.unit.$error }"/>
            <label for="unit" class="!bg-slate-900"
                   :class="{ 'has-error': $vulidate.unit.$error }">Unit</label>
          </FloatLabel>
          <template v-if="$vulidate.unit.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vulidate.unit.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="flex flex-col gap-0.5">
          <FloatLabel variant="on">
            <InputNumber v-allow-digits input-id="minimum" v-model="ad.minimum" autocomplete="off"
                         class="w-full"
                         input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="minimum" class="!bg-slate-900">Minimum</label>
          </FloatLabel>
        </div>
        <div class="flex flex-col gap-0.5">
          <FloatLabel variant="on">
            <InputNumber v-allow-digits input-id="maximum" v-model="ad.maximum" autocomplete="off"
                         class="w-full"
                         input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
            <label for="maximum" class="!bg-slate-900">Maximum</label>
          </FloatLabel>
        </div>
      </div>
      <div>
        <FloatLabel variant="on">
          <Textarea v-auto-dir id="description" v-model="ad.description" rows="5" style="resize: none"
                    class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700"/>
          <label for="description" class="!bg-slate-900">Description</label>
        </FloatLabel>
      </div>
    </div>

    <button @click="addNewAd" :disabled="saving"
            class="btn btn-outline-primary flex flex-row gap-4 py-1 w-max self-end items-center">
      <svg v-if="saving" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
           class="lucide lucide-loader-icon lucide-loader animate-spin">
        <path d="M12 2v4"/>
        <path d="m16.2 7.8 2.9-2.9"/>
        <path d="M18 12h4"/>
        <path d="m16.2 16.2 2.9 2.9"/>
        <path d="M12 18v4"/>
        <path d="m4.9 19.1 2.9-2.9"/>
        <path d="M2 12h4"/>
        <path d="m4.9 4.9 2.9 2.9"/>
      </svg>
      Save
    </button>
  </Dialog>
</template>

<style>
@import "@/assets/primevue.css";
</style>
<style>
.p-dialog-header {
  @apply flex flex-row gap-10 !px-4 !py-0 border-b border-slate-700 h-full
}

.p-dialog-header-actions {
  @apply flex flex-row items-center gap-4 h-full
}
</style>
